/**
 * Mention Suggestion Configuration
 *
 * Configures the @mention autocomplete behavior for Tiptap:
 * - Triggers when user types "@"
 * - Fetches matching users from the API
 * - Renders the MentionList popup component
 * - Handles keyboard navigation and selection
 *
 * This configuration is passed to Mention.configure({ suggestion: ... }).
 *
 * How it works:
 * 1. User types "@" - onStart is called, popup created
 * 2. User continues typing - items() fetches users, onUpdate refreshes list
 * 3. User presses key - onKeyDown handles navigation
 * 4. User selects - command() inserts mention
 * 5. onExit cleans up the popup
 *
 * Requirements implemented:
 * - RICH-07: @mentions for collaborator tagging
 */

import { ReactRenderer } from '@tiptap/react';
import tippy, { type Instance } from 'tippy.js';
import {
  MentionList,
  type MentionUser,
  type MentionListRef,
} from '../MentionList';

export type MentionSuggestionMode = 'users' | 'nodes';

export type NodeMentionSuggestion = {
  id: string;
  label: string;
  type?: string;
  content?: string;
  imageUrl?: string;
};

type MentionStorage = {
  mentionMode?: MentionSuggestionMode;
  nodeMentions?: NodeMentionSuggestion[];
};

// ============================================================
// SUGGESTION CONFIGURATION
// ============================================================

/**
 * Mention suggestion configuration object.
 *
 * This is passed to Mention.configure({ suggestion: mentionSuggestion }).
 * See: https://tiptap.dev/api/extensions/mention#suggestion
 */
export const mentionSuggestion = {
  // --------------------------------------------------------
  // TRIGGER CHARACTER
  // --------------------------------------------------------

  /**
   * Character that triggers the mention autocomplete.
   * Standard is "@" for user mentions.
   */
  char: '@',

  // --------------------------------------------------------
  // MATCHING BEHAVIOR
  // --------------------------------------------------------

  /**
   * Don't allow spaces in the search query.
   * After a space, the mention search ends.
   * Example: "@John " ends the search at "John"
   */
  allowSpaces: false,

  // --------------------------------------------------------
  // FETCH USERS FROM API
  // --------------------------------------------------------

  /**
   * Fetch matching users from the API.
   *
   * Called each time the search query changes.
   * Returns array of MentionUser objects.
   *
   * @param query - The text typed after "@" (e.g., "jo" in "@jo")
   */
  items: async ({ query, editor }: { query: string; editor: any }): Promise<MentionUser[]> => {
    const storage = (editor?.storage ?? {}) as MentionStorage;
    const mode = storage.mentionMode ?? 'users';

    if (mode === 'nodes') {
      const suggestions = Array.isArray(storage.nodeMentions) ? storage.nodeMentions : [];
      const filtered = suggestions
        .filter((item) => item.label.toLowerCase().includes(query.toLowerCase()))
        .slice(0, 6);

      return filtered.map((item) => ({
        id: item.id,
        name: item.label,
        email: item.type || 'Node',
      }));
    }

    try {
      // Call the user search API
      // credentials: 'include' sends the session cookie for authentication
      const response = await fetch(
        `/api/users/search?q=${encodeURIComponent(query)}`,
        { credentials: 'include' }
      );

      // Handle API errors gracefully (return empty list instead of throwing)
      if (!response.ok) {
        console.error('[MentionSuggestion] API error:', response.status);
        return [];
      }

      // Parse response and return user list
      const data = await response.json();
      return data.data || [];
    } catch (error) {
      // Network or parsing error - return empty list
      console.error('[MentionSuggestion] Fetch error:', error);
      return [];
    }
  },

  // --------------------------------------------------------
  // RENDER POPUP
  // --------------------------------------------------------

  /**
   * Render function for the suggestion popup.
   *
   * Returns an object with lifecycle callbacks:
   * - onStart: Create and show the popup
   * - onUpdate: Update popup position and content
   * - onKeyDown: Handle keyboard navigation
   * - onExit: Destroy the popup
   */
  render: () => {
    // These are defined in the outer scope so all callbacks can access them
    let component: ReactRenderer<MentionListRef>;
    let popup: Instance[];

    return {
      /**
       * onStart: Called when suggestion is triggered (user types "@")
       * Creates the React component and tippy popup.
       */
      onStart: (props: any) => {
        // Create the React component renderer
        // This wraps our MentionList component for use with tippy
        component = new ReactRenderer(MentionList, {
          props,           // Pass items and command to MentionList
          editor: props.editor,
        });

        // If no cursor position, can't show popup
        if (!props.clientRect) {
          return;
        }

        // Create tippy popup positioned near the cursor
        popup = tippy('body', {
          // Position relative to cursor (via clientRect)
          getReferenceClientRect: props.clientRect,
          // Append to body (not inline) for proper z-index
          appendTo: () => document.body,
          // Our MentionList component is the popup content
          content: component.element,
          // Show immediately when created
          showOnCreate: true,
          // Allow clicking inside the popup
          interactive: true,
          // We control show/hide manually
          trigger: 'manual',
          // Position below the trigger, aligned to start
          placement: 'bottom-start',
        });
      },

      /**
       * onUpdate: Called when search results change
       * Updates the component props and popup position.
       */
      onUpdate: (props: any) => {
        // Pass new props to the MentionList component
        // This triggers a re-render with new items
        component.updateProps(props);

        // Update popup position if cursor moved
        if (!props.clientRect) {
          return;
        }

        popup[0]?.setProps({
          getReferenceClientRect: props.clientRect,
        });
      },

      /**
       * onKeyDown: Handle keyboard events
       * Delegates to MentionList for navigation.
       */
      onKeyDown: (props: any) => {
        // Escape key: hide the popup
        if (props.event.key === 'Escape') {
          popup[0]?.hide();
          return true;
        }

        // Delegate other keys to MentionList (arrows, enter)
        // The ref exposes onKeyDown for this purpose
        return component.ref?.onKeyDown(props) ?? false;
      },

      /**
       * onExit: Called when suggestion is dismissed
       * Cleans up the popup and React component.
       */
      onExit: () => {
        // Destroy tippy popup (removes from DOM)
        popup[0]?.destroy();
        // Destroy React component (cleanup effects, etc.)
        component.destroy();
      },
    };
  },
};
